import React, { FormEvent, useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Trash2, Edit2 } from 'lucide-react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { getMaxDimensions } from '../utils/dimension-validator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { Card, CardContent } from './ui/card';
import { Material, Quote, QuoteUpdatePayload } from '../types';
import * as materialsService from '../services/materials.service';

interface QuoteItemForm {
  id: string;
  materialId: string;
  width: number; // in meters
  height: number; // in meters
  thickness: number; // in mm
  quantity: number;
  color?: string;
  unitPrice: number;
  subtotal: number;
}

interface CustomItemForm {
  id: string;
  description: string;
  value: number;
}

interface EditQuoteDialogProps {
  quote: Quote | null;
  open: boolean;
  onClose: () => void;
  onSave: (payload: QuoteUpdatePayload) => Promise<void>;
  isUpdating: boolean;
  accessToken?: string;
}

export function EditQuoteDialog({ quote, open, onClose, onSave, isUpdating, accessToken }: EditQuoteDialogProps) {
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loadingMaterials, setLoadingMaterials] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [items, setItems] = useState<QuoteItemForm[]>([]);
  const [customItems, setCustomItems] = useState<CustomItemForm[]>([]);
  const [discount, setDiscount] = useState('');

  // Custom item form state
  const [customDescription, setCustomDescription] = useState('');
  const [customValue, setCustomValue] = useState('');
  const [editingCustomItemId, setEditingCustomItemId] = useState<string | null>(null);

  // Current item being added/edited
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [currentMaterialId, setCurrentMaterialId] = useState('');
  const [currentWidth, setCurrentWidth] = useState(''); // in cm for display
  const [currentHeight, setCurrentHeight] = useState(''); // in cm for display
  const [currentThickness, setCurrentThickness] = useState('');
  const [currentQuantity, setCurrentQuantity] = useState('1');
  const [currentColor, setCurrentColor] = useState('');
  const [currentPrice, setCurrentPrice] = useState(0);
  const [bypassDimensionLimits, setBypassDimensionLimits] = useState(false);

  const loadMaterials = useCallback(async () => {
    if (!accessToken) return;

    try {
      setLoadingMaterials(true);
      const data = await materialsService.fetchMaterials(accessToken);
      // Only show active materials (not deleted, not inactive)
      setMaterials(data.filter(m => m.active && !m.deletedAt));
    } catch (err) {
      setError('Erro ao carregar materiais');
    } finally {
      setLoadingMaterials(false);
    }
  }, [accessToken]);

  // Load materials when dialog opens
  useEffect(() => {
    if (open && accessToken) {
      loadMaterials();
    }
  }, [open, accessToken, loadMaterials]);

  // Initialize form when dialog opens
  useEffect(() => {
    if (quote && open) {
      // Convert existing items - ensure we have items array
      const existingItems: QuoteItemForm[] = (quote.items || []).map(item => ({
        id: item.id,
        materialId: item.materialId,
        width: item.width,
        height: item.height,
        thickness: item.thickness,
        quantity: item.quantity,
        color: item.color,
        unitPrice: item.unitPrice,
        subtotal: item.subtotal
      }));

      setItems(existingItems);

      // Convert existing custom items
      const existingCustomItems: CustomItemForm[] = (quote.customItems || []).map(item => ({
        id: item.id,
        description: item.description,
        value: item.value
      }));

      setCustomItems(existingCustomItems);

      // Format discount for display
      let discountDisplay = '';
      if (quote.discountType === 'percentage' && quote.discountValue) {
        discountDisplay = `${quote.discountValue}%`;
      } else if (quote.discountType === 'fixed' && quote.discountValue) {
        discountDisplay = String(quote.discountValue);
      }
      setDiscount(discountDisplay);
    } else if (!open) {
      // Clear form when dialog closes
      setItems([]);
      setCustomItems([]);
      setDiscount('');
      setCustomDescription('');
      setCustomValue('');
      setEditingCustomItemId(null);
    }
  }, [quote, open]); // Reinitialize whenever dialog opens or quote changes

  // Get current dimension limits based on material and thickness
  const currentDimensionLimits = useMemo(() => {
    const material = materials.find(m => m.id === currentMaterialId);
    const thickness = parseFloat(currentThickness);

    if (!material || !thickness || isNaN(thickness)) {
      return null;
    }

    const limits = getMaxDimensions(thickness, material.dimensionLimits);
    return {
      maxWidthCm: limits.maxWidth * 100,
      maxHeightCm: limits.maxHeight * 100
    };
  }, [currentMaterialId, currentThickness, materials]);

  // Auto-correct dimensions when thickness changes (skip if bypass enabled)
  useEffect(() => {
    if (!currentDimensionLimits || bypassDimensionLimits) {
      return;
    }

    const width = parseFloat(currentWidth);
    const height = parseFloat(currentHeight);

    if (!isNaN(width) && width > currentDimensionLimits.maxWidthCm) {
      setCurrentWidth(currentDimensionLimits.maxWidthCm.toFixed(1));
    }
    if (!isNaN(height) && height > currentDimensionLimits.maxHeightCm) {
      setCurrentHeight(currentDimensionLimits.maxHeightCm.toFixed(1));
    }
  }, [currentDimensionLimits, bypassDimensionLimits]);

  // Handle width change with auto-clamping (skip if bypass enabled)
  const handleWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (!value) {
      setCurrentWidth('');
      return;
    }

    const numValue = parseFloat(value);

    if (isNaN(numValue)) {
      return;
    }

    if (!bypassDimensionLimits && currentDimensionLimits && numValue > currentDimensionLimits.maxWidthCm) {
      setCurrentWidth(currentDimensionLimits.maxWidthCm.toFixed(1));
    } else {
      setCurrentWidth(value);
    }
  };

  // Handle height change with auto-clamping (skip if bypass enabled)
  const handleHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (!value) {
      setCurrentHeight('');
      return;
    }

    const numValue = parseFloat(value);

    if (isNaN(numValue)) {
      return;
    }

    if (!bypassDimensionLimits && currentDimensionLimits && numValue > currentDimensionLimits.maxHeightCm) {
      setCurrentHeight(currentDimensionLimits.maxHeightCm.toFixed(1));
    } else {
      setCurrentHeight(value);
    }
  };

  // Calculate price for current item
  useEffect(() => {
    const material = materials.find(m => m.id === currentMaterialId);
    if (!material) {
      setCurrentPrice(0);
      return;
    }

    const width = parseFloat(currentWidth);
    const height = parseFloat(currentHeight);
    const thickness = parseFloat(currentThickness);
    const quantity = parseInt(currentQuantity, 10);

    if (isNaN(width) || isNaN(height) || isNaN(thickness) || isNaN(quantity)) {
      setCurrentPrice(0);
      return;
    }

    if (width <= 0 || height <= 0 || thickness <= 0 || quantity <= 0) {
      setCurrentPrice(0);
      return;
    }

    const widthMeters = width / 100;
    const heightMeters = height / 100;
    const thicknessMeters = thickness / 1000;
    const volumeM3 = widthMeters * heightMeters * thicknessMeters;
    const densityKgM3 = material.density * 1000;
    const weightKg = volumeM3 * densityKgM3;
    const unitPrice = weightKg * material.pricePerKg;
    const subtotal = unitPrice * quantity;

    setCurrentPrice(subtotal);
  }, [currentMaterialId, currentWidth, currentHeight, currentThickness, currentQuantity, materials]);

  const handleAddOrUpdateItem = () => {
    const material = materials.find(m => m.id === currentMaterialId);
    if (!material) return;

    const width = parseFloat(currentWidth);
    const height = parseFloat(currentHeight);
    const thickness = parseFloat(currentThickness);
    const quantity = parseInt(currentQuantity, 10);

    if (isNaN(width) || isNaN(height) || isNaN(thickness) || isNaN(quantity)) {
      setError('Preencha todos os campos do item');
      return;
    }

    if (width <= 0 || height <= 0 || thickness <= 0 || quantity <= 0) {
      setError('Todos os valores devem ser maiores que zero');
      return;
    }

    if (currentPrice <= 0) {
      setError('Preço calculado inválido');
      return;
    }

    const itemData: QuoteItemForm = {
      id: editingItemId || `item-${Date.now()}-${Math.random()}`,
      materialId: currentMaterialId,
      width: width / 100, // Convert cm to meters
      height: height / 100,
      thickness,
      quantity,
      color: currentColor || undefined,
      unitPrice: currentPrice / quantity,
      subtotal: currentPrice
    };

    if (editingItemId) {
      // Update existing item
      setItems(items.map(item => item.id === editingItemId ? itemData : item));
    } else {
      // Add new item
      setItems([...items, itemData]);
    }

    // Reset form
    setEditingItemId(null);
    setCurrentMaterialId('');
    setCurrentWidth('');
    setCurrentHeight('');
    setCurrentThickness('');
    setCurrentQuantity('1');
    setCurrentColor('');
    setBypassDimensionLimits(false);
    setError(null);
  };

  const handleEditItem = (item: QuoteItemForm) => {
    setEditingItemId(item.id);
    setCurrentMaterialId(item.materialId);
    setCurrentWidth((item.width * 100).toString()); // Convert meters to cm
    setCurrentHeight((item.height * 100).toString());
    setCurrentThickness(item.thickness.toString());
    setCurrentQuantity(item.quantity.toString());
    setCurrentColor(item.color || '');
    setError(null);
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
    setCurrentMaterialId('');
    setCurrentWidth('');
    setCurrentHeight('');
    setCurrentThickness('');
    setCurrentQuantity('1');
    setCurrentColor('');
    setBypassDimensionLimits(false);
    setError(null);
  };

  const handleRemoveItem = (itemId: string) => {
    setItems(items.filter(item => item.id !== itemId));
  };

  const calculateItemsTotal = () => {
    return items.reduce((sum, item) => sum + item.subtotal, 0);
  };

  const calculateCustomItemsTotal = () => {
    return customItems.reduce((sum, item) => sum + item.value, 0);
  };

  // Capture margin rate once from initial quote state (not reactively)
  const marginRate = useMemo(() => {
    if (!quote) return 0;
    const baseAmount = quote.items.reduce((sum, item) => sum + item.subtotal, 0);
    const initialCustomTotal = (quote.customItems || []).reduce((sum, ci) => sum + (ci.value || 0), 0);
    const marginAmount = Math.max(quote.totalAmount - baseAmount - initialCustomTotal, 0);
    return baseAmount > 0 ? marginAmount / baseAmount : 0;
  }, [quote]);

  const calculateTotal = () => {
    return calculateItemsTotal() * (1 + marginRate) + calculateCustomItemsTotal();
  };

  // Calculate total with discount in real-time
  const totalWithDiscount = useMemo(() => {
    const baseTotal = calculateTotal();

    if (!discount || discount.trim() === '') {
      return baseTotal;
    }

    const trimmed = discount.trim();

    // Check if it's a percentage discount
    if (trimmed.endsWith('%')) {
      const percentValue = parseFloat(trimmed.slice(0, -1));
      if (isNaN(percentValue)) return baseTotal;
      const discountAmount = (baseTotal * percentValue) / 100;
      return Math.max(0, baseTotal - discountAmount);
    }

    // Otherwise it's a fixed discount
    const fixedValue = parseFloat(trimmed);
    if (isNaN(fixedValue)) return baseTotal;
    return Math.max(0, baseTotal - fixedValue);
  }, [items, customItems, discount, marginRate]);

  // Custom item handlers
  const handleAddOrUpdateCustomItem = () => {
    if (!customDescription.trim()) {
      setError('Digite uma descrição para o item personalizado');
      return;
    }

    const value = parseFloat(customValue);
    if (isNaN(value) || value <= 0) {
      setError('Digite um valor válido maior que zero');
      return;
    }

    const itemData: CustomItemForm = {
      id: editingCustomItemId || `custom-${Date.now()}-${Math.random()}`,
      description: customDescription.trim(),
      value
    };

    if (editingCustomItemId) {
      setCustomItems(customItems.map(item => item.id === editingCustomItemId ? itemData : item));
    } else {
      setCustomItems([...customItems, itemData]);
    }

    // Reset form
    setEditingCustomItemId(null);
    setCustomDescription('');
    setCustomValue('');
    setError(null);
  };

  const handleEditCustomItem = (item: CustomItemForm) => {
    setEditingCustomItemId(item.id);
    setCustomDescription(item.description);
    setCustomValue(item.value.toString());
    setError(null);
  };

  const handleCancelCustomItemEdit = () => {
    setEditingCustomItemId(null);
    setCustomDescription('');
    setCustomValue('');
    setError(null);
  };

  const handleRemoveCustomItem = (itemId: string) => {
    setCustomItems(customItems.filter(item => item.id !== itemId));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!quote) return;

    if (items.length === 0) {
      setError('Adicione pelo menos um item ao orçamento');
      return;
    }

    const payload: QuoteUpdatePayload = {
      items: items.map(item => ({
        materialId: item.materialId,
        width: item.width,
        height: item.height,
        thickness: item.thickness,
        quantity: item.quantity,
        color: item.color || undefined // Don't send empty string - backend validates min length 1
      })),
      customItems: customItems.map(item => ({
        description: item.description,
        value: item.value
      })),
      discount: discount || undefined
    };

    await onSave(payload);
  };

  const selectedMaterial = materials.find(m => m.id === currentMaterialId);
  const availableThicknesses = selectedMaterial?.availableThicknesses || [];
  const showColorField = selectedMaterial?.name.toLowerCase().includes('colorido');

  const total = calculateTotal();

  if (!quote) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Orçamento {quote.quoteNumber}</DialogTitle>
          <DialogDescription>
            Cliente: {quote.customerName} ({quote.customerEmail})
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          {/* Add Item Section */}
          <Card>
            <CardContent className="pt-6 space-y-4">
              <h3 className="text-sm font-medium">Adicionar/Editar Itens</h3>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="material">Material *</Label>
                  <select
                    id="material"
                    value={currentMaterialId}
                    onChange={(e) => setCurrentMaterialId(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    disabled={loadingMaterials}
                  >
                    <option value="">Selecione um material</option>
                    {materials.map((material) => (
                      <option key={material.id} value={material.id}>
                        {material.code} - {material.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="width">Largura (cm) *</Label>
                  <Input
                    id="width"
                    type="number"
                    step="0.1"
                    min="0.1"
                    max={bypassDimensionLimits ? undefined : currentDimensionLimits?.maxWidthCm}
                    value={currentWidth}
                    onChange={handleWidthChange}
                    placeholder="Ex: 100"
                  />
                  {currentDimensionLimits && !bypassDimensionLimits && (
                    <div className="text-xs text-muted-foreground">
                      Máx: {currentDimensionLimits.maxWidthCm.toFixed(0)}cm
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="height">Altura (cm) *</Label>
                  <Input
                    id="height"
                    type="number"
                    step="0.1"
                    min="0.1"
                    max={bypassDimensionLimits ? undefined : currentDimensionLimits?.maxHeightCm}
                    value={currentHeight}
                    onChange={handleHeightChange}
                    placeholder="Ex: 200"
                  />
                  {currentDimensionLimits && !bypassDimensionLimits && (
                    <div className="text-xs text-muted-foreground">
                      Máx: {currentDimensionLimits.maxHeightCm.toFixed(0)}cm
                    </div>
                  )}
                </div>

                {/* Bypass dimension limits checkbox - show when material is selected so user can enable before selecting thickness */}
                {currentMaterialId && (
                  <div className="sm:col-span-2 flex items-center gap-2">
                    <Checkbox
                      id="bypass-limits"
                      checked={bypassDimensionLimits}
                      onCheckedChange={(checked) => setBypassDimensionLimits(checked === true)}
                    />
                    <Label htmlFor="bypass-limits" className="text-sm font-normal cursor-pointer">
                      Dimensões customizadas (ignorar limites)
                    </Label>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="thickness">Espessura (mm) *</Label>
                  {availableThicknesses.length > 0 && !bypassDimensionLimits ? (
                    <select
                      id="thickness"
                      value={currentThickness}
                      onChange={(e) => setCurrentThickness(e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      <option value="">Selecione</option>
                      {availableThicknesses.map((t) => (
                        <option key={t} value={t}>
                          {t}mm
                        </option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      id="thickness"
                      type="number"
                      step="0.1"
                      min="0.1"
                      value={currentThickness}
                      onChange={(e) => setCurrentThickness(e.target.value)}
                      placeholder="Ex: 3"
                    />
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="quantity">Quantidade *</Label>
                  <Input
                    id="quantity"
                    type="number"
                    min="1"
                    value={currentQuantity}
                    onChange={(e) => setCurrentQuantity(e.target.value)}
                    placeholder="Ex: 2"
                  />
                </div>

                {showColorField && (
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="color">Cor</Label>
                    <Input
                      id="color"
                      value={currentColor}
                      onChange={(e) => setCurrentColor(e.target.value)}
                      placeholder="Ex: Vermelho"
                    />
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between pt-2">
                <div className="text-sm">
                  <span className="text-muted-foreground">Preço calculado: </span>
                  <span className="font-semibold">R$ {currentPrice.toFixed(2)}</span>
                </div>
                <div className="flex gap-2">
                  {editingItemId && (
                    <Button type="button" variant="outline" onClick={handleCancelEdit} size="sm">
                      Cancelar
                    </Button>
                  )}
                  <Button type="button" onClick={handleAddOrUpdateItem} size="sm">
                    <Plus className="mr-2 h-4 w-4" />
                    {editingItemId ? 'Atualizar Item' : 'Adicionar Item'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Items List */}
          {items.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium">Itens do Orçamento ({items.length})</h3>
              {items.map((item) => {
                const material = materials.find(m => m.id === item.materialId);
                return (
                  <Card key={item.id}>
                    <CardContent className="p-4 flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-1">
                        <div className="font-medium">{material?.name || 'Material'}</div>
                        <div className="text-sm text-muted-foreground">
                          {(item.width * 100).toFixed(1)}cm × {(item.height * 100).toFixed(1)}cm × {item.thickness}mm
                          {item.color && ` - ${item.color}`}
                        </div>
                        <div className="text-sm">
                          <span className="text-muted-foreground">Qtd: </span>
                          <span>{item.quantity}</span>
                          <span className="mx-2">•</span>
                          <span className="text-muted-foreground">Subtotal: </span>
                          <span className="font-semibold">R$ {(item.subtotal * (1 + marginRate)).toFixed(2)}</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditItem(item)}
                        >
                          <Edit2 className="h-4 w-4 text-primary" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveItem(item.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}

              <div className="flex justify-between items-center border-t pt-4">
                <span className="font-semibold">Subtotal Itens:</span>
                <span className="text-lg font-bold">R$ {(calculateItemsTotal() * (1 + marginRate)).toFixed(2)}</span>
              </div>
            </div>
          )}

          {/* Custom Items Section */}
          <Card>
            <CardContent className="pt-6 space-y-4">
              <h3 className="text-sm font-medium">Itens Personalizados</h3>
              <p className="text-xs text-muted-foreground">
                Adicione custos extras como frete, mão de obra, acabamentos, etc.
              </p>

              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="customDescription">Descrição *</Label>
                  <Input
                    id="customDescription"
                    value={customDescription}
                    onChange={(e) => setCustomDescription(e.target.value)}
                    placeholder="Ex: Frete, Corte especial, Acabamento"
                    maxLength={255}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="customValue">Valor (R$) *</Label>
                  <Input
                    id="customValue"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={customValue}
                    onChange={(e) => setCustomValue(e.target.value)}
                    placeholder="Ex: 50.00"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2">
                {editingCustomItemId && (
                  <Button type="button" variant="outline" onClick={handleCancelCustomItemEdit} size="sm">
                    Cancelar
                  </Button>
                )}
                <Button type="button" onClick={handleAddOrUpdateCustomItem} size="sm" variant="secondary">
                  <Plus className="mr-2 h-4 w-4" />
                  {editingCustomItemId ? 'Atualizar' : 'Adicionar Item'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Custom Items List */}
          {customItems.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium">Itens Personalizados ({customItems.length})</h3>
              {customItems.map((item) => (
                <Card key={item.id} className="border-l-4 border-l-blue-500">
                  <CardContent className="p-4 flex items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="font-medium">{item.description}</div>
                      <div className="text-sm">
                        <span className="font-semibold text-blue-600">R$ {item.value.toFixed(2)}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditCustomItem(item)}
                      >
                        <Edit2 className="h-4 w-4 text-primary" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveCustomItem(item.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}

              <div className="flex justify-between items-center border-t pt-4">
                <span className="font-semibold text-blue-600">Subtotal Personalizados:</span>
                <span className="text-lg font-bold text-blue-600">R$ {calculateCustomItemsTotal().toFixed(2)}</span>
              </div>
            </div>
          )}

          {/* Discount */}
          <div className="space-y-2">
            <Label htmlFor="discount">Desconto (opcional)</Label>
            <Input
              id="discount"
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
              placeholder='Digite "10%" para 10% ou "50" para R$ 50'
            />
            <p className="text-xs text-muted-foreground">
              Use "%" para desconto percentual (ex: "10%") ou valor fixo em reais (ex: "50")
            </p>
          </div>

          {/* Total with Discount */}
          <div className="space-y-2 border-t pt-4">
            <div className="flex justify-between items-center">
              <span className="text-lg font-semibold">Total Final:</span>
              <span className="text-2xl font-bold text-primary">R$ {totalWithDiscount.toFixed(2)}</span>
            </div>
            {discount && discount.trim() !== '' && (
              <p className="text-xs text-muted-foreground text-right">
                Desconto aplicado: {discount}
              </p>
            )}
          </div>

          <DialogFooter>
            <div className="flex flex-col-reverse sm:flex-row gap-2 w-full sm:w-auto">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isUpdating}
                className="w-full sm:w-auto"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isUpdating || items.length === 0}
                className="w-full sm:w-auto"
              >
                {isUpdating ? 'Salvando...' : 'Salvar Alterações'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
